import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Detail from '../../components/Details/Detail';
import { Container, Box, Tabs, Tab, Button } from '@mui/material';
import { useAuthReady } from '../../hooks/useAuthReady';
import { LicitacionesService } from '../../services/licitaciones.service';
import { type LicitacionDetalleUI } from '../../schemas/licitaciones/detalle';
import { useLoadingStore } from '../../store/loadingStore';
import BackButton from '../../components/Button/BackButton';
import { useAlertStore } from '../../store/alertStore';
import QuestionsAndAnswers from '../../components/Details/QuestionsAndAnswers';
import Archivos from '../../components/Details/Archivos';
import JsonMetadataTable from '../../components/Details/JsonMetadataTable';
import Notas from '../../components/Details/Notas';
import { useLicitacionesStore } from '../../store/licitacionesStore';

interface Nota {
  id: string;
  fecha_creacion: string;
  mensaje: string;
  activo: boolean;
  usuario_autor: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LicitacionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [licitacion, setLicitacion] = useState<LicitacionDetalleUI | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const isAuthReady = useAuthReady();
  const setLoading = useLoadingStore((state) => state.setLoading);
  const showAlert = useAlertStore((state) => state.showAlert);
  const fetchLicitaciones = useLicitacionesStore((state) => state.fetchLicitaciones);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  

  const fetchDetail = useCallback(async () => {
    if (isAuthReady && id) {
      try {
        setLoading(true);
        const response = await LicitacionesService.fetchLicitacionDetail(id);
        setLicitacion(response);
      } catch (error) {
        console.error('Error fetching licitacion detail:', error);
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        showAlert(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    }
  }, [id, isAuthReady, setLoading, showAlert]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleStatusChange = (newStatus: string) => {
    if (licitacion) {
      setLicitacion({
        ...licitacion,
        estado_licitacion: newStatus
      });
    }
  };

  const handlePostulacionChange = async () => {
    if (!licitacion) return;
    try {
      const response = await LicitacionesService.postular(licitacion.codigo_externo);
      showAlert(response.message, 'success');
      await fetchDetail();
      await fetchLicitaciones({ silent: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al postular';
      showAlert(errorMessage, 'error');
    }
  };

  const handleAdjudicacionChange = async () => {
    if (!licitacion) return;
    try {
      const response = await LicitacionesService.adjudicar(licitacion.codigo_externo);
      showAlert(response.message, 'success');
      await fetchDetail();
      await fetchLicitaciones({ silent: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al adjudicar';
      showAlert(errorMessage, 'error');
    }
  };

  const handleFetchMensajes = async () => {
    if (!licitacion) return [];
    try {
      return await LicitacionesService.listarMensajes(licitacion.id);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar las notas';
      showAlert(errorMessage, 'error');
      return [];
    }
  };

  const handleCrearMensaje = async (mensaje: string): Promise<Nota[]> => {
    if (!licitacion) return [];
    try {
      await LicitacionesService.crearMensaje(licitacion.id, mensaje);
      showAlert('Nota creada correctamente', 'success');
      const nuevosMensajes = await LicitacionesService.listarMensajes(licitacion.id);
      return nuevosMensajes;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al crear la nota';
      showAlert(errorMessage, 'error');
      throw error;
    }
  };

  const handleEliminarMensaje = async (mensajeId: string): Promise<Nota[]> => {
    if (!licitacion) return [];
    try {
      await LicitacionesService.eliminarMensaje(mensajeId);
      showAlert('Nota eliminada correctamente', 'success');
      const nuevosMensajes = await LicitacionesService.listarMensajes(licitacion.id);
      return nuevosMensajes;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al eliminar la nota';
      showAlert(errorMessage, 'error');
      throw error;
    }
  };

  //const getVisitaTerreno = (contenido: string | null | undefined) => {
  //  if (!contenido) return 'No obligatoria';
  //  const match = contenido.match(/VISITA A TERRENO[^\n]*/i);
  //  return match ? match[0] : 'No obligatoria';
  //};
  
  const getVisitaTerreno = (contenido: string | null | undefined) => {
    if (!contenido) return 'No obligatoria';

    // 1. Busca cualquier variante de visita a/en terreno (obligatoria o no)
    const regex = /(VISITA\s+(?:A|EN)\s+TERRENO[^\n]*)/gi;
    let match;
    let resultadosEncontrados: string[] = [];

    while ((match = regex.exec(contenido)) !== null) {
      const lineaEncontrada = match[1];
      resultadosEncontrados.push(lineaEncontrada);

      // Si la línea es muy corta (solo el título tipo "VISITA A TERRENO OBLIGATORIA"),
      // capturamos un poco del texto que sigue inmediatamente después en el contenido.
      if (lineaEncontrada.length < 40) {
        const indiceSiguiente = regex.lastIndex;
        const textoSiguiente = contenido.substring(indiceSiguiente, indiceSiguiente + 250);
        
        // Limpiamos saltos de línea extra y buscamos una fecha o dirección cercana
        const proximaLineaMatch = textoSiguiente.match(/([^\n]+)/);
        if (proximaLineaMatch && proximaLineaMatch[1].trim().length > 3) {
          resultadosEncontrados.push(proximaLineaMatch[1].trim());
        }
      }
    }

    if (resultadosEncontrados.length > 0) {
      // Unimos el título y la descripción obtenida de forma limpia
      return resultadosEncontrados.join(' - ');
    }

    return 'No obligatoria';
  };

  if (!isAuthReady || !licitacion) {
    return (
      <Container maxWidth={false}>
      </Container>
    );
  }

  //console.log("URL Ficha Oficial en Frontend:", licitacion.url_ficha_oficial);
  //console.log("Objeto licitacion completo en Frontend:", licitacion);
  return (
    <Container maxWidth={false}>
      <Box sx={{ mb: 3, mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <BackButton to="/" label="Volver al listado" />
      </Box>
      
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        display: { xs: 'none', sm: 'block' }
      }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              minHeight: '48px',
              padding: '12px 16px'
            },
            '& .Mui-selected': {
              color: '#1976d2 !important',
            }
          }}
        >
          <Tab label="Resumen" />
          <Tab label="Preguntas" />
          <Tab label="Archivos" />
          <Tab label="Datos Extra" />
          <Tab label="Notas" />
        </Tabs>
      </Box>

      <Box 
        sx={{ 
          display: { xs: 'flex', sm: 'none' },
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          '& .MuiButton-root': {
            flex: '1 1 auto',
            textTransform: 'none',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            padding: '6px 8px'
          }
        }}
      >
        <Button 
          variant={tabValue === 0 ? "contained" : "outlined"}
          onClick={() => setTabValue(0)}
          size="small"
        >
          Resumen
        </Button>
        <Button 
          variant={tabValue === 1 ? "contained" : "outlined"}
          onClick={() => setTabValue(1)}
          size="small"
        >
          Preguntas
        </Button>
        <Button 
          variant={tabValue === 2 ? "contained" : "outlined"}
          onClick={() => setTabValue(2)}
          size="small"
        >
          Archivos
        </Button>
        <Button 
          variant={tabValue === 3 ? "contained" : "outlined"}
          onClick={() => setTabValue(3)}
          size="small"
        >
          Datos Extra
        </Button>
        <Button 
          variant={tabValue === 4 ? "contained" : "outlined"}
          onClick={() => setTabValue(4)}
          size="small"
        >
          Notas
        </Button>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Detail 
          nombre={licitacion.nombre}
          resumen_llm={licitacion.resumen_llm}
          resumen={licitacion.resumen ?? ""}
          resumen_general={licitacion.resumen_general}
          fuentes_resumen_general={licitacion.fuentes_resumen_general}
          fecha_creacion={licitacion.fecha_creacion ?? ""}
          estado_licitacion={licitacion.estado_licitacion}
          codigo_externo={licitacion.codigo_externo}
          onStatusChange={handleStatusChange}
          codigo_estado={licitacion.codigo_estado}
          puntaje={licitacion.puntaje}
          fecha_cierre={licitacion.fecha_cierre}
          monto_estimado={licitacion.monto_estimado}
          moneda={licitacion.moneda}
          fetchDetail={fetchDetail}
          puntaje_archivos={licitacion.puntaje_archivos}
          resumen_archivos_llm={licitacion.resumen_archivos_llm}
          requisitos_postulacion={licitacion.requisitos_postulacion}
          criterios_evaluacion={licitacion.criterios_evaluacion}
          resumen_procesado_filtro_palabras={licitacion.resumen_procesado_filtro_palabras}
          visita_terreno={getVisitaTerreno(licitacion.contenido_ficha)}
          archivos_procesados={licitacion.archivos_procesados}
          resumen_procesado_filtro_llm={licitacion.resumen_procesado_filtro_llm}
          postulado={licitacion.postulado}
          adjudicado={licitacion.adjudicado}
          fecha_postulacion={licitacion.fecha_postulacion}
          fecha_adjudicacion={licitacion.fecha_adjudicacion}
          onPostulacionChange={handlePostulacionChange}
          onAdjudicacionChange={handleAdjudicacionChange}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <QuestionsAndAnswers 
          preguntas_y_respuestas={licitacion.preguntas_y_respuestas}
          entityType="licitaciones"
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>

        <Archivos 
          archivos={licitacion.archivos} 
          entityType="licitaciones" 
          codigoExterno={licitacion.codigo_externo} 
          tematicaId={licitacion.tematica_id}
          urlFichaOficial={licitacion.url_ficha_oficial ?? undefined}
          onRefresh={fetchDetail} 
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <JsonMetadataTable 
          jsonMetadata={licitacion.json_metadata}
          entityType="licitaciones"
          resumen={licitacion.resumen ?? ""}
          resumen_llm={licitacion.resumen_llm ?? ""}
          resumen_archivos_llm={licitacion.resumen_archivos_llm ?? ""}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Notas 
          entityType="licitaciones"
          id={licitacion.id}
          onFetchMensajes={handleFetchMensajes}
          onCrearMensaje={handleCrearMensaje}
          onEliminarMensaje={handleEliminarMensaje}
        />
      </TabPanel>
    </Container>
  );
};

export default LicitacionDetail; 